import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Typography,
  Box,
} from '@mui/material';
import { useTranslate } from 'react-admin';

interface MessageEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (newBody: string) => Promise<boolean>;
  currentBody: string;
  /** Remaining time to edit (e.g., "5 min") or null if no limit */
  remainingTime?: string | null;
}

/**
 * Dialog for editing a message body.
 * Shows a text field with the current message body and allows editing.
 */
export function MessageEditDialog({
  open,
  onClose,
  onSave,
  currentBody,
  remainingTime,
}: MessageEditDialogProps) {
  const translate = useTranslate();
  const [body, setBody] = useState(currentBody);
  const [isLoading, setIsLoading] = useState(false);

  // Reset body when dialog opens with new content
  useEffect(() => {
    if (open) {
      setBody(currentBody);
    }
  }, [open, currentBody]);

  const handleSave = async () => {
    if (body.trim() === currentBody.trim()) {
      onClose();
      return;
    }

    setIsLoading(true);
    const success = await onSave(body.trim());
    setIsLoading(false);

    if (success) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
  };

  const hasChanges = body.trim() !== currentBody.trim();
  const isValid = body.trim().length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
    >
      <DialogTitle>
        {translate('resources.message.dialogs.editTitle')}
      </DialogTitle>
      <DialogContent>
        {remainingTime && (
          <Box mb={2}>
            <Typography variant="caption" color="warning.main">
              {translate('resources.message.dialogs.editTimeRemaining', {
                time: remainingTime,
                _: `You have ${remainingTime} to edit this message`,
              })}
            </Typography>
          </Box>
        )}
        <TextField
          autoFocus
          multiline
          minRows={2}
          maxRows={8}
          fullWidth
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={translate('resources.message.dialogs.editPlaceholder')}
          disabled={isLoading}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          {translate('ra.action.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isLoading || !hasChanges || !isValid}
          startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
        >
          {translate('ra.action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
